import * as React from "react"
import {connect, Dispatch} from "react-redux";
import {createLead, updateLeadAssignees, updateLeadWatchers} from "../../../../store/action/lead/lead";
import LeadData from "../../../../model/lead/LeadData";
import {OnFailure, OnSuccess, OnSuccessData} from "../../../../store/action/actionHelpers";
import Lead from "../../../../model/lead/Lead";
import Toast from "../../../helper/Toast";
import SiteData from "../../../../model/site/SiteData";
import Site from "../../../../model/site/Site";
import {createSite} from "../../../../store/action/site/site";
import AddLeadFormView from "./AddLeadFormView";
import {option, Option} from "ts-option";
import Appointment from "../../../../model/appointment/Appointment";
import {createAppointment} from "../../../../store/action/appointment/appointment";
import {api} from "../../../../api/api";
import MentionUtils from "../../../../utils/MentionUtils";
import _ = require("underscore");

interface ComponentProps {
	/**
	 * On success callback.
	 */
	onSuccess?: OnSuccessData<Lead>;
}

interface DispatchProps {
	/**
	 * Create lead action.
	 * @param {LeadData} lead lead data to send.
	 * @param {OnSuccess} onSuccess on lead creation success callback.
	 * @param {OnFailure} onFailure on lead creation failure callback.
	 */
	createLead: (lead: LeadData, onSuccess: OnSuccessData<Lead>, onFailure: OnFailure) => void
	/**
	 * Create site action.
	 * @param {SiteData} site site data to send.
	 * @param {OnSuccessData<Site>} onSuccess on site creation success callback.
	 * @param {OnFailure} onFailure on site creation failure callback.
	 */
	createSite: (site: SiteData, onSuccess: OnSuccessData<Site>, onFailure: OnFailure) => void
	/**
	 * Update watchers action
	 * @param {string} leadId Lead Id.
	 * @param {string[]} watchers Array of users Id's.
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	updateWatchers: (leadId: string, watchers: string[]) => void
	/**
	 * Update assignees action
	 * @param {string} leadId Lead Id.
	 * @param {Option<string>} agentId agent identifier.
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	updateAssignees: (leadId: string, agentId: Option<string>) => void
	/**
	 * Create appointment
	 * @param {string} leadId
	 * @param {Appointment} appointment
	 */
	createAppointment: (leadId: string, appointment: Appointment) => void;
}

interface State {
	/**
	 * Indicates if lead is saving currently.
	 */
	isLeadSaving: boolean;
}

type Props = ComponentProps & DispatchProps;

/**
 * Add new lead form.
 */
class AddLeadForm extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {isLeadSaving: false};
		this.onSuccess = this.onSuccess.bind(this);
		this.onFailure = this.onFailure.bind(this);
		this.createLeadWithSite = this.createLeadWithSite.bind(this);
	}

	render() {
		return (
			<AddLeadFormView isLoading={this.state.isLeadSaving}
							 onSubmit={this.createLeadWithSite}
							 label={""}/>
		);
	}

	/**
	 * On successfully added action.
	 * @param {Lead} lead Currently added lead.
	 */
	private onSuccess(lead: Lead): void {
		this.setState({isLeadSaving: false});
		Toast.success("Lead saved", "Lead submission");
		if (this.props.onSuccess != undefined) {
			this.props.onSuccess(lead);
		}
	}

	/**
	 * On adding new lead failure.
	 */
	private onFailure(error: any) {
		//TODO if any other error can return 412 we need to handle response data and specific error code or something like that
		if (error.response.status == 412) {
			Toast.error("Sorry, no default brand is set for your organisation. Please contact administrator.",
				"Create Lead error");
		}
		else {
			Toast.error("Sorry, something went wrong. Try again later.", "Create Lead error");
		}
		this.setState({isLeadSaving: false});
	}

	/**
	 * Creates lead.
	 * @param {LeadData} leadData Lead data.
	 * @param {SiteData} siteData SiteData.
	 * @param {string[]} watchers
	 * @param {Option<Appointment[]>} appointments
	 * @param {Option<string>} agentId
	 */
	private createLeadWithSite(leadData: LeadData,
		siteData: Option<SiteData>,
		watchers: string[],
		appointments: Option<Appointment[]>,
		agentId: Option<string>) {
		this.setState({isLeadSaving: true}, () => {
			siteData.map(sd => {
				this.props.createSite(sd, (site) => {
						this.createLead(leadData.copy({siteId: option(site.id)}), watchers, appointments, agentId);
					},
					() => {
						this.setState({isLeadSaving: false});
						Toast.error("Sorry, something went wrong. Try again later.", "Create Site error");
					}
				);
			}).orElse(() => {
				this.createLead(leadData, watchers, appointments, agentId);
				return null;
			})
		});
	}

	/**
	 * Create lead
	 * @param {LeadData} leadData Lead data.
	 * @param {string[]} watchers
	 * @param {Option<Appointment[]>} appointments
	 * @param {Option<string>} agentId
	 */
	createLead(leadData: LeadData,
		watchers: string[],
		appointments: Option<Appointment[]>,
		agentId: Option<string>) {
		this.props.createLead(leadData, (lead) => {
			this.props.updateAssignees(lead.id, agentId);
			this.props.updateWatchers(lead.id, watchers);
			appointments.map(t => t.map(a => this.props.createAppointment(lead.id, a)));
			this.onSuccess(lead);
			this.mentionUsers(lead);
		}, this.onFailure)
	}

	private mentionUsers(lead: Lead) {
		lead.data.extendedDescription.map(d => {
			const uniqueUsers = _.uniq(MentionUtils.getMentions(d).map(m => m.id));
			if (uniqueUsers.length)
				api.instance.leadApi.mentionUsers(lead.id, uniqueUsers).catch(() => Toast.error("Mention users error"));
		})
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		createLead: (lead: LeadData, onSuccess: OnSuccessData<Lead>, onFailure: OnFailure) => dispatch(
			createLead(lead, onSuccess, onFailure)
		),
		createSite: (site: SiteData, onSuccess: OnSuccessData<Site>, onFailure: OnFailure) => dispatch(
			createSite(site, onSuccess, onFailure)
		),
		updateWatchers: (leadId: string, watchers: string[]) => dispatch(
			updateLeadWatchers(
				leadId,
				watchers,
				() => {},
				() => Toast.error("Sorry, something went wrong. Try again later.", "Lead watchers update")
			)
		),
		updateAssignees: (leadId: string, agentId: Option<string>) => dispatch(
			updateLeadAssignees(
				leadId,
				agentId,
				() => {},
				() => Toast.error("Sorry, something went wrong. Try again later.", "Lead assignees update")
			)
		),
		createAppointment: (leadId: string, appointment: Appointment) => dispatch(
			createAppointment(
				leadId,
				appointment,
				() => Toast.success("Appointment created", "Appointment created"),
				() => Toast.error("Sorry, something went wrong. Try again later.", "Create appointment")
			)
		)
	}
}

export default connect(null, mapDispatchToProps)(AddLeadForm)